<template>
  <panel
    title="Account Tags"
    subtitle="Group similar users using tags to improve search and segmentation"
    no-padding
  >
    <template v-if="$can('update', 'Account')" #buttons>
      <b-button
        rounded
        class="is-bold"
        type="is-primary"
        :loading="isLoading"
        @click="saveAccountTags"
      >
        Save
      </b-button>
    </template>

    <div class="pad-b pad-x">
      <validation-provider
        v-slot="{ errors }"
        name="accountTags"
        :rules="{
          required: !!selectedTags.length
        }"
      >
        <b-checkbox
          :value="!hasTagMoreThan50Characters || null"
          :class="['is-hidden', { 'invalid': errors.length }]"
        />
        <b-field>
          <div class="is-flex-column">
            <p v-if="errors.length" class="is-size-7 has-text-danger">
              Tags must be less than 50 characters
              <b-icon icon="exclamation-circle" type="is-danger" size="is-small" />
            </p>
            <b-taglist class="dist-x-sm">
              <b-tag
                v-for="tag in selectedTags"
                :key="`tag${tag.id}`"
                v-tippy="{
                  content: tag.name.length > 50
                    ? 'This tag is more than 50 characters. Tags need to be less than 50 characters to be valid.'
                    : 'This tag already exists, but has been deactived. Please reactivate it in order to add it to this guest.',
                  onShow: () => tag.name.length > 50 || inactiveTagNames.includes(tag.name.toLowerCase())
                }"
                ellipsis
                closable
                attached
                size="is-medium"
                :type="tag.name.length > 50 || inactiveTagNames.includes(tag.name.toLowerCase()) ? 'is-danger' : 'is-primary'"
                :close-type="tag.name.length > 50 || inactiveTagNames.includes(tag.name.toLowerCase()) ? 'is-danger' : 'is-primary'"
                @close="handleSelectTag(tag)"
              >
                <p class="mar-r-xs mar-none">
                  <span class="has-text-weight-bold">{{ tag.name }}</span>
                </p>
              </b-tag>
            </b-taglist>

            <div class="is-flex">
              <b-autocomplete
                ref="autocomplete"
                v-model="query"
                icon="search"
                class="is-inline has-icon-right has-dividers has-extra-shadow tag-search flex-grow"
                expanded
                :data="filteredAvailableTags"
                open-on-focus
                max-height="325"
                placeholder="Select or add tags..."
                clearable
                clear-on-selected
                :icon-right="query ? 'times-circle' : ''"
                icon-right-clickable
                :disabled="!$can('update', 'Account')"
                :loading="isFetching"
                @select="($event) => handleSelectTag($event)"
              >
                <template #empty>
                  There are no tags matching {{ query ? `'${query}'` : 'your search' }}
                </template>
                <template #footer>
                  <a v-if="query && queryHasNoMatch && $can('update', 'Account')" @click="createTag">
                    <span>+ Add {{ query }}... </span>
                  </a>
                </template>
                <template slot-scope="props">
                  <div :class="['tag-option is-grid col-4', {'selected': !!findTag(props.option)}]">
                    <div class="is-flex-column col-span-3">
                      <p>{{ props.option.name }}</p>
                    </div>
                    <b-icon
                      v-if="!!findTag(props.option)"
                      size="is-small"
                      type="is-primary"
                      icon="check"
                      class="justify-self-end align-self-center"
                    />
                  </div>
                </template>
              </b-autocomplete>

              <b-button
                v-if="selectedTags.length && $can('update', 'Account')"
                class="clear-tags-btn mar-l"
                type="is-primary"
                inverted
                @click="clearAllTags"
              >
                Clear All Tags
              </b-button>
            </div>
          </div>
        </b-field>
      </validation-provider>
    </div>
  </panel>
</template>

<script>
  import AccountTag from '@/helpers/classes/AccountTag';

  import UserAttribute from '@/store/classes/UserAttribute';
  import AccountUserAttribute from '@/store/classes/AccountUserAttribute';


  export default {
    name: 'AccountTagsPanel',

    props: {
      registeredGuest: {
        type: Object,
        required: true
      }
    },

    data: () => ({
      query: null,
      selectedTags: [],
      isLoading: false
    }),

    computed: {
      isFetching() {
        return UserAttribute.$state().fetching;
      },

      queryHasNoMatch() {
        return !this.filteredAvailableTags.some(tag => tag.name.toLowerCase() === this.query?.toLowerCase());
      },

      activeTags() {
        const userAttributes = UserAttribute
          .query()
          .where('isActive', true)
          .get()
          .map(userAttribute => (
            new AccountTag(userAttribute)
          ));

        const newTags = this.selectedTags.filter(tag => tag.isNewTag);
        return [...newTags, ...userAttributes];
      },

      inactiveTagNames() {
        return UserAttribute.query().where('isActive', false).get().map(attribute => attribute.name.toLowerCase());
      },

      filteredAvailableTags() {
        if (!this.query) return this.activeTags;
        return this.activeTags.filter(tag => tag.name.toLowerCase().includes(this.query.toLowerCase()));
      },

      hasTagMoreThan50Characters() {
        return this.selectedTags.some(tag => tag?.name.length > 50);
      }
    },

    async created() {
      await this.onCreated();
    },

    methods: {
      async onCreated() {
        await this.fetchUserAttributes();

        // Initialize selected tags based on user's current attributes
        // Map the account user attributes to AccountTag objects
        this.selectedTags = this.registeredGuest.userAttributeIds.map((userAttributeId) => {
          const userAttribute = UserAttribute.find(userAttributeId);
          return new AccountTag({
            name: userAttribute?.name || '',
            accountId: this.registeredGuest.id,
            userAttributeId
          });
        });
      },

      async fetchUserAttributes() {
        try {
          await UserAttribute.fetchAttributes();
        }
        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: 'There was an error fetching user attributes'
            }
          });
        }
      },

      findTag(tag) {
        return this.selectedTags.find(selectedTag => selectedTag.name.toLowerCase() === tag.name.toLowerCase());
      },

      createTag() {
        const tag = new AccountTag({
          name: this.query,
          accountId: this.registeredGuest.id
        });
        this.handleSelectTag(tag);
      },

      handleSelectTag(tag) {
        if (tag) {
          const existingTag = this.findTag(tag);
          if (existingTag) {
            this.removeTag(existingTag.id);
          }
          else {
            this.addTag(tag);
          }
          this.query = null;
        }
      },

      addTag(tag) {
        this.selectedTags.push(tag);
      },

      removeTag(tagId) {
        const index = this.selectedTags.findIndex(tag => tag.id === tagId);
        if (index !== -1) {
          this.selectedTags.splice(index, 1);
        }
      },

      clearAllTags() {
        this.selectedTags = [];
      },

      async saveAccountTags() {
        try {
          this.isLoading = true;

          // Get the user attribute IDs from the selected tags
          const userAttributeIds = this.selectedTags.filter(tag => !tag.isNewTag).map(tag => tag.userAttributeId);

          // Create any new user attributes
          let errorsCount = 0;
          const newTagPromises = this.selectedTags
            .filter(tag => tag.isNewTag)
            .map(async (tag) => {
              try {
                const { userAttribute } = await UserAttribute.createAttribute({
                  name: tag.name,
                  isActive: true
                });
                return userAttribute.id;
              }
              catch (error) {
                errorsCount++;
                return null;
              }
            });

          // Wait for all new user attributes to be created
          const newUserAttributeIds = await Promise.all(newTagPromises);

          // Filter out any nulls (failed creations)
          const validNewUserAttributeIds = newUserAttributeIds.filter(id => id !== null);

          // Combine existing and new user attribute IDs
          const allUserAttributeIds = [...userAttributeIds, ...validNewUserAttributeIds];

          // Update the account's user attributes
          await AccountUserAttribute.updateAccountAttributes(
            this.registeredGuest.id,
            allUserAttributeIds
          );

          this.$_onRequestSuccess({
            toastOptions: { message: `${allUserAttributeIds.length} account tags successfully added to user!` }
          });

          if (errorsCount > 0) {
            this.$_onRequestError({
              toastOptions: {
                message: `There was an error adding ${errorsCount} account tags to this user. Hover over the red tags to see what the issues are.`
              }
            });
          }
        }
        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: 'There was an error updating account tags'
            }
          });
        }
        finally {
          this.isLoading = false;
        }
      }
    }
  };
</script>

<style lang='sass' scoped>
  .tag-search
    ::v-deep
      .dropdown-menu
        padding-top: 1px

  .selected
    color: $primary

  ::v-deep
    .tags
      margin-bottom: 0 !important

    .tags.has-addons
      margin-bottom: 0 !important

    .tag .has-ellipsis
      max-width: 25rem
</style>
